import React from 'react'
import { SettingsPanelProps } from './types'
import {
  PanelContainer,
  PanelHeader,
  HeaderTitle,
  PropertiesContent,
  PropertySection,
  SectionHeader,
  SectionTitle,
  CollapseIcon,
  SectionContent,
  PropertyGroup,
  Label,
  Select,
  Input,
  Checkbox,
  FileInputWrapper,
  FileInputLabel,
  HiddenFileInput,
  InfoText,
  FlexRow,
  Button,
  ColorPickerWrapper,
  ColorPreview,
  ColorDetails,
  ColorLabel,
  ColorValue,
  ColorInput,
} from '../styled'

const SettingsPanel: React.FC<SettingsPanelProps> = ({
  mapSettings,
  onSettingsChange,
  collapsedSections,
  toggleSection,
}) => {
  return (
    <PanelContainer>
      <PanelHeader>
        <HeaderTitle>Floorplan Settings</HeaderTitle>
      </PanelHeader>
      <PropertiesContent>
        <PropertySection>
          <SectionHeader
            isCollapsed={collapsedSections.general}
            onClick={() => toggleSection('general')}>
            <SectionTitle>General Settings</SectionTitle>
            <CollapseIcon isCollapsed={collapsedSections.general}>
              ▼
            </CollapseIcon>
          </SectionHeader>
          <SectionContent isCollapsed={collapsedSections.general}>
            <PropertyGroup>
              <Label>Units</Label>
              <Select
                value={mapSettings.units}
                onChange={e =>
                  onSettingsChange?.({
                    units: e.target.value as 'feet' | 'meters',
                  })
                }>
                <option value="feet">Feet</option>
                <option value="meters">Meters</option>
              </Select>
            </PropertyGroup>
            <PropertyGroup>
              <Label>
                Scale (pixels per{' '}
                {mapSettings.units === 'feet' ? 'foot' : 'meter'})
              </Label>
              <Input
                type="number"
                value={mapSettings.scale}
                onChange={e =>
                  onSettingsChange?.({ scale: Number(e.target.value) })
                }
                min="1"
                max="100"
              />
            </PropertyGroup>
            <PropertyGroup>
              <Label>
                <Checkbox
                  checked={mapSettings.allowDragging}
                  onChange={e =>
                    onSettingsChange?.({ allowDragging: e.target.checked })
                  }
                />
                Allow Dragging
              </Label>
            </PropertyGroup>
            <PropertyGroup>
              <Label>
                <Checkbox
                  checked={mapSettings.showGrid}
                  onChange={e =>
                    onSettingsChange?.({ showGrid: e.target.checked })
                  }
                />
                Show Grid
              </Label>
            </PropertyGroup>
            <PropertyGroup>
              <Label>
                <Checkbox
                  checked={mapSettings.snapToGrid}
                  onChange={e =>
                    onSettingsChange?.({ snapToGrid: e.target.checked })
                  }
                />
                Snap to Grid
              </Label>
            </PropertyGroup>
            <PropertyGroup>
              <Label>
                <Checkbox
                  checked={mapSettings.showDimensions}
                  onChange={e =>
                    onSettingsChange?.({ showDimensions: e.target.checked })
                  }
                />
                Show Dimensions
              </Label>
            </PropertyGroup>
          </SectionContent>
        </PropertySection>

        <PropertySection>
          <SectionHeader
            isCollapsed={collapsedSections.background}
            onClick={() => toggleSection('background')}>
            <SectionTitle>Background Image</SectionTitle>
            <CollapseIcon isCollapsed={collapsedSections.background}>
              ▼
            </CollapseIcon>
          </SectionHeader>
          <SectionContent isCollapsed={collapsedSections.background}>
            <PropertyGroup>
              <Label>Upload Image</Label>
              <FileInputWrapper>
                <FileInputLabel htmlFor="bg-image-upload">
                  📁 Choose image file
                </FileInputLabel>
                <HiddenFileInput
                  id="bg-image-upload"
                  accept="image/*"
                  onChange={e => {
                    const file = e.target.files?.[0]
                    if (file) {
                      const reader = new FileReader()
                      reader.onload = event => {
                        const imageUrl = event.target?.result as string
                        onSettingsChange?.({ backgroundImage: imageUrl })
                      }
                      reader.readAsDataURL(file)
                    }
                  }}
                />
              </FileInputWrapper>
              {mapSettings.backgroundImage && <InfoText>Image loaded</InfoText>}
            </PropertyGroup>
            {mapSettings.backgroundImage && (
              <>
                <PropertyGroup>
                  <Label>Position</Label>
                  <FlexRow>
                    <div style={{ flex: 1 }}>
                      <Label>X</Label>
                      <Input
                        type="number"
                        value={mapSettings.backgroundPosition?.x || 0}
                        onChange={e =>
                          onSettingsChange?.({
                            backgroundPosition: {
                              ...(mapSettings.backgroundPosition || {
                                x: 0,
                                y: 0,
                              }),
                              x: Number(e.target.value),
                            },
                          })
                        }
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <Label>Y</Label>
                      <Input
                        type="number"
                        value={mapSettings.backgroundPosition?.y || 0}
                        onChange={e =>
                          onSettingsChange?.({
                            backgroundPosition: {
                              ...(mapSettings.backgroundPosition || {
                                x: 0,
                                y: 0,
                              }),
                              y: Number(e.target.value),
                            },
                          })
                        }
                      />
                    </div>
                  </FlexRow>
                </PropertyGroup>
                <PropertyGroup>
                  <Label>Size</Label>
                  <FlexRow>
                    <div style={{ flex: 1 }}>
                      <Label>Width</Label>
                      <Input
                        type="number"
                        value={mapSettings.backgroundSize?.width || 800}
                        onChange={e =>
                          onSettingsChange?.({
                            backgroundSize: {
                              ...(mapSettings.backgroundSize || {
                                width: 800,
                                height: 600,
                              }),
                              width: Number(e.target.value),
                            },
                          })
                        }
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <Label>Height</Label>
                      <Input
                        type="number"
                        value={mapSettings.backgroundSize?.height || 600}
                        onChange={e =>
                          onSettingsChange?.({
                            backgroundSize: {
                              ...(mapSettings.backgroundSize || {
                                width: 800,
                                height: 600,
                              }),
                              height: Number(e.target.value),
                            },
                          })
                        }
                      />
                    </div>
                  </FlexRow>
                </PropertyGroup>
                <PropertyGroup>
                  <Label>
                    Opacity (
                    {Math.round((mapSettings.backgroundOpacity ?? 0.5) * 100)}%)
                  </Label>
                  <Input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={mapSettings.backgroundOpacity ?? 0.5}
                    onChange={e =>
                      onSettingsChange?.({
                        backgroundOpacity: Number(e.target.value),
                      })
                    }
                  />
                </PropertyGroup>
                <Button
                  variant="danger"
                  onClick={() =>
                    onSettingsChange?.({
                      backgroundImage: undefined,
                      backgroundPosition: undefined,
                      backgroundSize: undefined,
                      backgroundOpacity: undefined,
                    })
                  }>
                  Remove Background
                </Button>
              </>
            )}
          </SectionContent>
        </PropertySection>

        <PropertySection>
          <SectionHeader
            isCollapsed={collapsedSections.canvasBackground}
            onClick={() => toggleSection('canvasBackground')}>
            <SectionTitle>Canvas Background Colour</SectionTitle>
            <CollapseIcon isCollapsed={collapsedSections.canvasBackground}>
              ▼
            </CollapseIcon>
          </SectionHeader>
          <SectionContent isCollapsed={collapsedSections.canvasBackground}>
            <PropertyGroup>
              <Label>Background Colour</Label>
              <ColorPickerWrapper>
                <ColorPreview
                  color={mapSettings.backgroundColor || '#ffffff'}
                />
                <ColorDetails>
                  <ColorLabel>Current Colour</ColorLabel>
                  <ColorValue>
                    {mapSettings.backgroundColor || '#ffffff'}
                  </ColorValue>
                </ColorDetails>
                <ColorInput
                  value={mapSettings.backgroundColor || '#ffffff'}
                  onChange={e =>
                    onSettingsChange?.({ backgroundColor: e.target.value })
                  }
                />
              </ColorPickerWrapper>
            </PropertyGroup>
            {mapSettings.backgroundColor &&
              mapSettings.backgroundColor !== '#ffffff' && (
                <Button
                  variant="danger"
                  onClick={() =>
                    onSettingsChange?.({ backgroundColor: undefined })
                  }>
                  Reset to White
                </Button>
              )}
          </SectionContent>
        </PropertySection>
      </PropertiesContent>
    </PanelContainer>
  )
}

export { SettingsPanel }
